// src/pages/ExamTermStart.jsx
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { API } from '../../api';
import { isAndroid, isSebPlatform } from '../../utils/device';
import { FaWindows, FaApple } from "react-icons/fa6";


export default function ExamTermStart() {
    const { termId } = useParams(); // /egzamin/termin/:termId/start
    const nav = useNavigate();

    const [loading, setLoading] = useState(true);
    const [msg, setMsg] = useState('');
    const [meta, setMeta] = useState(null); // { attempt_id, exam_title, start_at, duration_min, proctoring }

    // wykrywanie SEB (backend)
    const [sebCheck, setSebCheck] = useState({ checked: false, isSEB: false, via: 'none', ua: '' });

    const sebPreferred = isSebPlatform();
    const android = isAndroid();

    // Plan B (SEB-eligible -> problem/Android)
    const [problemText, setProblemText] = useState('');
    const problemValid = problemText.trim().length >= 5;

    useEffect(() => {
        let mounted = true;

        async function run() {
            setMsg('');
            setLoading(true);
            try {
                // równolegle: inicjacja attemptu + sprawdzenie SEB
                const [initRes, sebRes] = await Promise.allSettled([
                    API.initiateAttempt(termId),
                    // preferuj metodę w API; jeśli jej nie ma, fallback do fetch
                    (API.sebCheck ? API.sebCheck() : fetch('/api/seb/check', { credentials: 'include' }).then(r => r.json()))
                ]);

                if (mounted) {
                    if (initRes.status === 'fulfilled') setMeta(initRes.value);
                    else setMsg(initRes.reason?.message || 'Błąd inicjacji egzaminu');

                    if (sebRes.status === 'fulfilled') {
                        const { isSEB, via, ua } = sebRes.value || {};
                        setSebCheck({ checked: true, isSEB: !!isSEB, via: via || 'none', ua: ua || '' });
                    } else {
                        // jeśli endpoint padł, nie blokuj – traktuj jako "nie-SEB"
                        setSebCheck({ checked: true, isSEB: false, via: 'none', ua: '' });
                    }
                }
            } finally {
                if (mounted) setLoading(false);
            }
        }

        run();
        return () => { mounted = false; };
    }, [termId]);

    async function downloadSeb() {
        if (!meta?.attempt_id) return;
        try {
            const res = await API.getSebFile(meta.attempt_id);
            if (!res.ok) throw new Error('Nie udało się pobrać pliku SEB');
            const blob = await res.blob();
            const a = document.createElement('a');
            a.href = URL.createObjectURL(blob);
            a.download = `exam-${meta.attempt_id}.seb`;
            a.click();
            setTimeout(() => URL.revokeObjectURL(a.href), 2000);
        } catch (e) {
            setMsg(e.message || 'Błąd pobierania pliku SEB');
        }
    }

    async function continueInBrowser() {
        if (!meta?.attempt_id) return;
        try {
            setMsg('');
            const { token } = await API.getSession(meta.attempt_id);
            nav(`/student/egzamin/${meta.attempt_id}/oczekiwanie?st=${encodeURIComponent(token)}`);
        } catch (e) {
            setMsg(e.message || 'Błąd przygotowania sesji');
        }
    }


    async function continueWithNote() {
        if (!meta?.attempt_id || !problemValid) return;
        try {
            setMsg('');
            const { token } = await API.getSession(meta.attempt_id, { note: problemText.trim() });
            nav(`/student/egzamin/${meta.attempt_id}/oczekiwanie?st=${encodeURIComponent(token)}`);
        } catch (e) {
            setMsg(e.message || 'Błąd przygotowania sesji');
        }
    }

    if (loading) return <div style={{ maxWidth: 720, margin: '24px auto' }}>Ładowanie…</div>;
    if (!meta) return <div style={{ maxWidth: 720, margin: '24px auto', color:'#a00' }}>{msg || 'Brak danych egzaminu'}</div>;

    const startAt = meta.start_at ? new Date(meta.start_at).toLocaleString() : '—';
    const dur = meta.duration_min != null ? `${meta.duration_min} min` : '—';
    const securityLevel = String(meta.level || (meta.proctoring === false ? 'brak' : '')).trim().toLowerCase();
    const sebRequired = securityLevel !== 'brak';

    if (!sebRequired) {
        return (
            <div style={{ maxWidth: 1000, padding: '0 16px', marginTop:80, display: 'grid', gap: 16 }}>
                <h1>{meta.exam_title || 'Egzamin'} — informacje i przygotowanie</h1>

                <div style={{ border: '1px solid #eee', borderRadius: 12, padding: 16, display:'grid', gap:8, background:'white' }}>
                    <div><b>Termin:</b> {startAt}</div>
                    <div><b>Czas trwania:</b> {dur}</div>
                </div>

                <div style={{ border:'2px solid #0a66', background:'white', borderRadius:14, padding:16 }}>
                    <h2 style={{ marginTop:0 }}>Przeglądarka SEB nie jest wymagana</h2>
                    <p style={{ marginTop:0 }}>
                        Dla tego terminu możesz korzystać ze standardowej przeglądarki. Aplikacja zabezpiecza podejście i zapisuje listę naruszeń do weryfikacji po egzaminie.
                    </p>
                    <div data-tour="start-button" style={{ display:'flex', gap:8, flexWrap:'wrap', marginTop:8 }}>
                        <button className={'but-cta'} onClick={continueInBrowser}>Przejdź dalej</button>
                    </div>
                </div>

                {msg && <div style={{ color:'#a00' }}>{msg}</div>}
            </div>
        );
    }

    // --- WIDOK: jeśli jesteśmy w SEB, pokaż prosty komunikat + CTA ---
    if (sebCheck.checked && sebCheck.isSEB) {
        return (
            <div style={{ maxWidth: 900, margin: '24px auto', display: 'grid', gap: 16 }}>
                <h1>{meta.exam_title || 'Egzamin'} — informacje i przygotowanie</h1>

                <div style={{ border: '1px solid #eee', borderRadius: 12, padding: 16, display:'grid', gap:8 }}>
                    <div><b>Termin:</b> {startAt}</div>
                    <div><b>Czas trwania:</b> {dur}</div>
                </div>

                <div style={{ border:'2px solid #0a66', background:'white', borderRadius:14, padding:16 }}>
                    <h2 style={{ marginTop:0 }}>Jesteś w trybie bezpiecznym (SEB)</h2>
                    <p style={{ marginTop:0 }}>
                        Wykryliśmy przeglądarkę Safe Exam Browser. Możesz przejść bezpośrednio do poczekalni egzaminu.
                    </p>
                    <div data-tour="start-button" style={{ display:'flex', gap:8, flexWrap:'wrap', marginTop:8 }}>
                        <button className={'but-cta'}  onClick={continueInBrowser}>Przejdź do egzaminu</button>
                    </div>
                    <div style={{ marginTop:8, fontSize:12, color:'#456' }}>
                        <span>Detekcja: {sebCheck.via === 'headers' ? 'nagłówki SEB' : 'user-agent'}</span>
                    </div>
                </div>

                {msg && <div style={{ color:'#a00' }}>{msg}</div>}
            </div>
        );
    }

    // --- WIDOK standardowy: wybór środowiska (gdy NIE w SEB) ---
    return (
        <div style={{ maxWidth: 1000,  padding: '0 16px', marginTop:80, display: 'grid', gap: 16 }}>
            <h1>{meta.exam_title || 'Egzamin'} — informacje i przygotowanie</h1>

            <div style={{ border: '1px solid #eee', borderRadius: 12, padding: 16, display:'grid', gap:8, background:'white'}}>
                <div><b>Termin:</b> {startAt}</div>
                <div><b>Czas trwania:</b> {dur}</div>
            </div>
            <div style={{ display:'grid', gap:12 }}>
                {/* Kafelek SEB – wyróżniony na Windows/macOS/iOS */}
                {sebPreferred && (
                    <div style={{  background:'linear-gradient(to bottom, #0f172a 0 0%, #003671 100% 100%)', borderRadius:14, padding:16, boxShadow: '0 1px 2px rgba(0,0,0,.15)' }}>
                        <h2 data-tour="seb-download" style={{ marginTop:10, color:'white' }}>Pobierz przeglądarkę SEB</h2>
                        <p style={{ marginTop:10, color:'white' }}>
                            Na tym urządzeniu zalecamy użycie <b>Safe Exam Browser</b>. Pobierz przeglądarkę SEB klikając w link poniżej, a nastepnie zainstaluj na swoim urządzeniu i wróć na tą stronę w celu pobrania pliku konfiguracyjnego SEB poniżej.

                        </p>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px,1fr))',
                            gap: 16, marginBottom: 24, marginTop:20, justifyContent: 'center', alignItems: 'center' }}>
                            <a href={'https://github.com/SafeExamBrowser/seb-win-refactoring/releases/download/v3.10.0/SEB_3.10.0.826_SetupBundle.exe'} rel="noreferrer" target="_blank">
                                <button
                                    style={{
                                        textAlign: 'center', border: '1px solid white', borderRadius: 12,
                                        padding: 16, background: 'none', cursor: 'pointer',width:'250px',
                                        boxShadow: '0 1px 2px rgba(0,0,0,.05)', placeItems: 'center', alignItems: 'center'
                                    }}
                                > <FaWindows style={{ width: 50, height: 50, opacity: 0.95, fill:'white' }} aria-hidden />
                                    <div style={{ fontSize: 18, fontWeight: 600, color:'white', marginTop:10 }}>Windows</div>
                                </button>
                            </a>
                            <a href={'https://github.com/SafeExamBrowser/seb-mac/releases/download/3.6/SafeExamBrowser-3.6.dmg'} rel="noreferrer" target="_blank">
                                <button
                                    style={{
                                        textAlign: 'center', border: '1px solid #e5e7eb', borderRadius: 12,
                                        padding: 16, background: 'none', cursor: 'pointer',width:'250px',
                                        boxShadow: '0 1px 2px rgba(0,0,0,.05)'
                                    }}
                                > <FaApple style={{ width: 50, height: 50, opacity: 0.95, fill:'white' }} aria-hidden />
                                    <div style={{ fontSize: 18, fontWeight: 600, color:'white', marginTop: 10 }}>macOS</div>
                                </button>
                            </a>
                            <a href={'https://itunes.apple.com/app/safeexambrowser/id1155002964?mt=8'} rel="noreferrer" target="_blank">
                                <button
                                    style={{
                                        textAlign: 'center', border: '1px solid #e5e7eb', borderRadius: 12,
                                        padding: 16, background: 'none', cursor: 'pointer',
                                        boxShadow: '0 1px 2px rgba(0,0,0,.05)', width:'250px'
                                    }}
                                > <FaApple style={{ width: 50, height: 50, opacity: 0.95, fill:'white' }} aria-hidden />
                                    <div style={{ fontSize: 18, fontWeight: 600, color:'white', marginTop:10 }}>iOS (App Store)</div>
                                </button>
                            </a>
                        </div>

                        {/* Plan B pod kafelkiem SEB */}
                        <div style={{ borderTop:'1px solid #cde', paddingTop:12 }}>
                            <div style={{ fontWeight:600, marginBottom:6, color:'white', marginTop:8 }}>
                                Jeśli korzystasz z systemu Android nie musisz pobierać przeglądarki SEB.
                            </div>

                        </div>
                    </div>
                )}

            </div>

            {/* Sekcja wyboru środowiska */}
            <div style={{ display:'grid', gap:12, paddingBottom: 50 }}>
                {/* Kafelek SEB – wyróżniony na Windows/macOS/iOS */}
                {sebPreferred && (
                    <div style={{  background:'white', borderRadius:14, padding:16, border: '1px solid #eee' }}>
                        <h2 style={{ marginTop:0 }}>Tryb bezpieczny (SEB)</h2>
                        <p>
                            Jeśli przeglądarka SEB jest już zainstalowana na Twoim komputerze <b>pobierz plik konfiguracyjny poniżej i otwórz go w SEB</b>,
                            aby przejść do poczekalni egzaminu.
                        </p>
                        <div data-tour="seb-link" style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:12 }}>
                            <button onClick={downloadSeb}
                                style={{
                                    textAlign: 'center', border: '1px solid #e5e7eb', borderRadius: 12,
                                    padding: 16, background: 'none', cursor: 'pointer',
                                    boxShadow: '0 1px 2px rgba(0,0,0,.05)', width:'250px', backgroundColor:'#0f172a', marginTop:20
                                }}
                            >

                                <div style={{ fontSize: 30, fontWeight: 600, color:'white', marginTop:20 }}>SEB</div>
                                <div style={{ fontSize: 18, fontWeight: 400, color:'white', marginTop:10, marginBottom:20 }}>Pobierz plik konfiguracyjny egzaminu</div>
                            </button>
                        </div>

                        {/* Plan B pod kafelkiem SEB */}
                        <div style={{ borderTop:'1px solid #cde', paddingTop:12 }}>
                            <div style={{ fontWeight:600, marginBottom:6 }}>
                                Korzystasz z Androida lub napotkałeś problem?
                            </div>
                            <p style={{ marginTop:0 }}>
                                Opisz krótko sytuację i przejdź w przeglądarce (informacja będzie widoczna dla prowadzącego/administratora).
                            </p>
                            <textarea
                                required
                                minLength={5}
                                placeholder="Np. brak możliwości instalacji SEB, błąd uruchomienia… (min. 5 znaków)"
                                value={problemText}
                                onChange={e=>setProblemText(e.target.value)}
                                style={{ width:'100%', minHeight:80, padding:8, borderRadius:8, border:'1px solid #ccd' }}
                            />
                            <div style={{ marginTop:8 }}>
                                <button
                                    className={'but-cta'}
                                    onClick={continueWithNote}
                                    disabled={!problemValid}
                                    title={!problemValid ? 'Wpisz co najmniej 5 znaków' : ''}
                                >
                                    Przejdź w przeglądarce
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Kafelek Android / przeglądarka – pełny, bez wymaganej notatki */}
                {android && (
                    <div style={{ border:'1px solid #ddd', borderRadius:14, padding:16, background:'#fff' }}>
                        <h3 style={{ marginTop:0 }}>Korzystam z Androida</h3>
                        <p>Kontynuuj w tej przeglądarce. Przejdziesz do poczekalni, gdzie sprawdzisz kamerę i poczekasz do startu.</p>
                        <button className={'but-cta'} data-tour="start-button" onClick={continueInBrowser}>Przejdź dalej w przeglądarce</button>
                    </div>
                )}
            </div>

            {msg && <div style={{ color:'#a00' }}>{msg}</div>}
        </div>
    );
}
